import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { downloadFile, openPdfInNewTab } from "@shared/utils/utils";
import {
  approveGenerateBills,
  downloadBillTemplate,
  downloadfailedList,
  getBillCycleDetails,
  getBillCycleList,
  getBillCycleMeterDashboard,
  getBillCycleMeterReading,
  getBillData,
  getBillGenerationSummary,
  getBillTemplates,
  getBulkBillData,
  getCategoryConfigMap,
  getConsumerCount,
  getConsumerDashboard,
  getConsumerListMissingPlan,
  getConsumerListMissingTariff,
  getConsumerListViewData,
  getMeterReadingMissing,
  getMeterReadingPendingValidation,
  getMeterTypeChoices,
  getOutstandingBills,
  getOutstandingSummary,
  getPlanDashboard,
  getPlansList,
  getRatesList,
  getSubCategoryConfigMap,
  getTerritoryConfigMap,
  getUtilityServices,
  submitConsumerSelection,
  updateConsumerSelection,
} from "./api";
import { useQueryClient } from "@tanstack/react-query";

export const useUtilityServices = () => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.utilityServiceList(),
      getRemoteUtilityId(),
    ],
    () => getUtilityServices()
  );
};

export const useCategoryConfigMap = () => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.categoryList(),
      getRemoteUtilityId(),
    ],
    () => getCategoryConfigMap()
  );
};

export const useSubCategoryConfigMap = (codes: string[]) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.bulkBillGeneration.subCategoryList(), codes],
    () => getSubCategoryConfigMap(codes),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useAreaList = () => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.bulkBillGeneration.areaList(),
      getRemoteUtilityId(),
    ],
    () => getTerritoryConfigMap(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useSubAreaList = (areaList: string[]) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.bulkBillGeneration.subAreaList(), areaList],
    () => getTerritoryConfigMap("sub_area", areaList)
  );
};

export const usePremiseList = (subAreaList: string[]) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.bulkBillGeneration.premiseList(), subAreaList],
    () => getTerritoryConfigMap("premise", subAreaList)
  );
};

export const useBillCycleList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBillGeneration.billCycleList(),
    () => getBillCycleList()
  );
};

export const useBillCycleDetails = (billId: any) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.bulkBillGeneration.billCycleDetails(),
      billId,
    ],
    () => getBillCycleDetails(billId),
    {
      enabled: !!billId,
    }
  );
};

export const useConsumerCount = (premise: string[], category: any[]) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.consumerCount(),
      premise,
      category,
    ],
    () => getConsumerCount(category, premise),
    {
      enabled: premise.length > 0 && category.length > 0,
    }
  );
};

// export const useUpdateConsumerSelection = (billId: string | number) => {
//   const { toast } = useToast();

//   return useSmartMutation(
//     async (payload: any) => {
//       return updateConsumerSelection(billId, payload);
//     },
//     {
//       onError: (error: any) => {
//         toast({ title: "Error while updating the request" });
//       },
//     }
//   );
// };

export const useUpdateConsumerSelection = (billId: string | number) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useSmartMutation(
    (payload: any) => {
      console.log("📤 Updating consumer selection with payload:", payload);
      return updateConsumerSelection(billId, payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Consumer selection updated successfully",
        });

        // 🔄 Invalidate only the meterReadingMissing query
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.bx.bulkBillGeneration.meterReadingMissing(),
          exact: false,
        });
        console.log("🔄 Refetched meterReadingMissing query");
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to update consumer selection",
          variant: "destructive",
        });
      },
    }
  );
};


export const useSubmitConsumerSelection = () => {
  const { toast } = useToast();

  return useSmartMutation(
    async (payload: any) => {
      return submitConsumerSelection(payload);
    },
    {
      onError: (error: any) => {
        toast({ title: "Error while creating the request" });
      },
    }
  );
};

export const usePlanDashboard = (billCycleId: string | number) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.planDashboard(),
      getRemoteUtilityId(),
      billCycleId,
    ],
    () => getPlanDashboard(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useConsumerListViewData = (
  billCycleId: string | number,
  billDraftId: string,
  page: number
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.consumerListViewData(),
      getRemoteUtilityId(),
      billCycleId,
      page,
    ],
    () => getConsumerListViewData(billCycleId,billDraftId, page),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useConsumerDashboard = (billCycleId: string | number) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.consumerDashboard(),
      getRemoteUtilityId(),
      billCycleId,
    ],
    () => getConsumerDashboard(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useConsumerListMissingPlan = (
  billCycleId: string | number,
  billDraftId:string,
  page: number
) => {
  console.log("vcallingn this hook :", billCycleId, page);
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.consumerListMissingPlan(),
      getRemoteUtilityId(),
      billCycleId,
      page,
    ],
    () => getConsumerListMissingPlan(billCycleId,billDraftId, page),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useConsumerListMissingTariff = (
  billCycleId: string | number,
  billDraftId:string,
  page: number
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.consumerMissingTariff(),
      getRemoteUtilityId(),
      billCycleId,
      page,
    ],
    () => getConsumerListMissingTariff(billCycleId,billDraftId, page),
    {
      enabled: !!billCycleId,
    }
  );
};

export const usePlansList = (page: number = 1, limit: number = 50) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.plansList(),
      getRemoteUtilityId(),
      page,
      limit,
    ],
    () => getPlansList(page, limit),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useRatesList = (page: number = 1, limit: number = 50) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.ratesList(),
      getRemoteUtilityId(),
      page,
      limit,
    ],
    () => getRatesList(page, limit),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useOutstandingBills = (
  billCycleId: string | number,
  limit: number = 5
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.outstandingBills(),
      getRemoteUtilityId(),
      billCycleId,
      limit,
    ],
    () => getOutstandingBills(billCycleId, limit),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      enabled: !!billCycleId,
    }
  );
};

export const useOutstandingSummary = (billCycleId: string | number) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.outstandingSummary(),
      getRemoteUtilityId(),
      billCycleId,
    ],
    () => getOutstandingSummary(billCycleId),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      enabled: !!billCycleId,
    }
  );
};

export const useBillCycleMeterReading = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.meterReading(),
      getRemoteUtilityId(),
      queryParams,
    ],
    () => getBillCycleMeterReading(queryParams),
    {
      enabled: !!queryParams,
    }
  );
};

export const useMeterTypeChoices = () => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.meterTypeChoices(),
      getRemoteUtilityId(),
    ],
    () => getMeterTypeChoices()
  );
};

export const useBillCycleMeterReadingMissing = (
  billCycleId: string,
  billDraftId:string,
  page: number
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.meterReadingMissing(),
      getRemoteUtilityId(),
      billCycleId,
      page,
    ],
    () => getMeterReadingMissing(billCycleId, billDraftId,page),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useMeterReadingPendingValidation = (
  billCycleId: string,
  billDraftId:string,
  page: number
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.meterReadingPendingValidation(),
      getRemoteUtilityId(),
      billCycleId,
      page,
    ],
    () => getMeterReadingPendingValidation(billCycleId,billDraftId, page),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useBillCycleMeterDashboard = (billCycleId: string | number) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.bulkBillGeneration.meterDashboard(),
      getRemoteUtilityId(),
      billCycleId,
    ],
    () => getBillCycleMeterDashboard(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useBillGenerationSummary = (billRunId: string) => {
  console.log("billRunId", billRunId);
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.bulkBillGeneration.billGenerationSummary(),
      billRunId,
    ],
    () => getBillGenerationSummary(billRunId),
    {
      enabled: !!billRunId,
    }
  );
};

export const useBillData = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.bulkBillGeneration.billData(), queryParams],
    () => getBillData(queryParams),
    {
      enabled: !!queryParams,
    }
  );
};

export const useApproveGenerateBill = (runID: string, id:any) => {
  const { toast } = useToast();
  return useSmartMutation(
    async (payload: any) => {
      return approveGenerateBills(payload, runID, id);
    },
    {
      onError: (error: any) => {
        toast({
          title: "Error while approving bill",
          description: error.message,
          variant: "destructive",
        });
      },
      onSuccess: () => {
        toast({ title: "Bill approved successfully!" });
      },
    }
  );
};

export const useDownloadCSV = () => {
  const {toast} = useToast();
  return useSmartMutation(
    async ({
      taskID,
      filename = "bill_generation_errors.csv",
    }: {
      taskID: string;
      filename?: string;
    }) => {
      const blob = await downloadfailedList(taskID);
      toast({
        title: "Export Initiated",
        description: blob?.message,
      });
    }
  );
};

export const useDownloadBill = () => {
  return useSmartMutation(
    async ({
      billId,
      filename = "template.pdf",
    }: {
      billId: string;
      filename?: string;
    }) => {
      const blob = await downloadBillTemplate(billId);
      downloadFile(blob, filename);
      return blob;
    }
  );
};

export const useViewBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    openPdfInNewTab(blob);
    return blob;
  });
};

export const useBillTemplates = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBillGeneration.billTemplates(),
    () => getBillTemplates(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useBulkBillData = (billId: string) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.bulkBillGeneration.bulkBillData(), billId],
    () => getBulkBillData(billId),
    {
      enabled: !!billId,
    }
  );
};
